import { Component, computed, inject, Signal } from '@angular/core';
import { BackgroundColorDirective, ButtonDirective, ColorDirective, TypographyDirective } from '@dartcounter/ui';
import { SmartDeviceApiService } from '@dc-api/smart-device.api.service';
import { SmartDeviceFirmware, User } from '@dc-core/dc-backend/dc-classes';
import { VersionHelper } from '@dc-core/dc-helpers/version.helper';
import { AlertPayload, AlertService } from '@dc-core/dc-services/alert.service';
import { IonMenu, NavController } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { UserStateService } from 'src/app/modules/user/services/user-state.service';
import { OldIconComponent } from 'src/app/shared/components/icon/icon.component';
import { OptionButtonComponent } from 'src/app/shared/components/option-button/option-button.component';
import { ToggleSwitchComponent } from 'src/app/shared/components/toggle-switch/toggle-switch.component';
import { PromptDialogComponent } from 'src/app/shared/dialogs/prompt/prompt.dialog';
import { SearchPlayerDialogComponent } from 'src/app/shared/dialogs/search-player/search-player.dialog';
import { HideScrollbarDirective } from 'src/app/shared/directives/hide-scrollbar.directive';
import { ModalService } from 'src/app/shared/services/modal.service';
import { centerDialogClasses, slideDialogClasses } from 'src/app/shared/utils/dialog.utils';
import { environment } from 'src/environments/environment';

import {
    FirmwareUpdateMode,
    SmartDeviceFirmwareUpdateDialogComponent,
    SmartDeviceFirmwareUpdateDialogPayload,
} from '../../../dialogs/smart-device-firmware-update/smart-device-firmware-update.dialog';
import {
    SmartDevicesChangeFirmwareComponent,
    SmartDevicesChangeFirmwarePayload,
} from '../../../dialogs/smart-devices-change-firmware/smart-devices-change-firmware.component';
import { ConnectionStatus, DartCounterSmartDevice, OmniFirmwareSettings } from '../../../models/smart-devices.models';
import { SmartDevicesStateService } from '../../../services/smart-devices-state.service';
import { OmniCalibrationImagesComponent } from '../../dialogs/omni-calibration-images/omni-calibration-images.component';
import { OmniCommunicationService } from '../../services/omni-communication.service';
import { OmniStateService } from '../../services/omni-state.service';
import { OverTheAirUpdateService } from '../../services/over-the-air-update.service';
import { OmniBetaOptInComponent, OmniBetaOptInResponse } from '../omni-beta-opt-in/omni-beta-opt-in.component';

@Component({
    selector: 'app-omni-settings-menu',
    standalone: true,
    imports: [
        IonMenu,
        BackgroundColorDirective,
        ColorDirective,
        TypographyDirective,
        ButtonDirective,
        OptionButtonComponent,
        ToggleSwitchComponent,
        TranslateModule,
        OldIconComponent,
        HideScrollbarDirective,
    ],
    templateUrl: './omni-settings-menu.component.html',
    styleUrl: './omni-settings-menu.component.css',
})
export class OmniSettingsMenuComponent {
    public readonly isProduction = environment.production;
    public readonly isWeb = environment.isWeb;
    public readonly ConnectionStatus = ConnectionStatus;

    public authService: AuthService = inject(AuthService);

    private userStateService: UserStateService = inject(UserStateService);
    private omniStateService: OmniStateService = inject(OmniStateService);
    private modalService: ModalService = inject(ModalService);
    private smartDeviceApiService: SmartDeviceApiService = inject(SmartDeviceApiService);
    private smartDevicesStateService: SmartDevicesStateService = inject(SmartDevicesStateService);
    private omniCommunicationService: OmniCommunicationService = inject(OmniCommunicationService);
    private overTheAirUpdateService: OverTheAirUpdateService = inject(OverTheAirUpdateService);
    private alertService: AlertService = inject(AlertService);
    private navController: NavController = inject(NavController);

    public firmwareSettings: Signal<OmniFirmwareSettings> = computed(() =>
        this.omniStateService.currentFirmwareSettings()
    );

    public selectedOmni: Signal<DartCounterSmartDevice> = computed(() => this.omniStateService.currentSmartDevice());

    public isAdmin: Signal<boolean> = computed(() =>
        this.smartDevicesStateService.isAdmin(this.omniStateService.currentSmartDeviceNetwork(), this.selectedOmni())
    );

    public isBeta: Signal<boolean> = computed(
        () => !!this.userStateService.currentGroups().find((group) => group.name === 'omni_beta')
    );

    public isDev: Signal<boolean> = computed(
        () => !!this.userStateService.currentGroups().find((group) => group.name === 'omni_dev')
    );

    public isTester: Signal<boolean> = computed(
        () => !!this.userStateService.currentGroups().find((group) => group.name === 'omni_tester')
    );

    public currentConnectionStatus: Signal<ConnectionStatus> = computed(() =>
        this.omniCommunicationService.currentConnectionStatus()
    );

    public versionHelper = VersionHelper;

    public toggleSaveAllImages(status: boolean): void {
        this.omniStateService.updateFirmwareSettings(() => ({
            saveAllImages: status,
        }));
    }

    public toggleSaveToArchive(status: boolean): void {
        this.omniStateService.updateFirmwareSettings(() => ({
            saveToArchive: status,
        }));
    }

    public shareOmni(): void {
        this.modalService
            .openModal<any, User>({
                component: SearchPlayerDialogComponent,
                componentProps: {},
                cssClass: slideDialogClasses,
                backdropDismiss: true,
            })
            .then((user) => {
                if (user) {
                    this.smartDeviceApiService
                        .inviteUserToSmartDeviceNetwork({
                            smartDeviceNetworkId: this.selectedOmni().network_id,
                            user_id: user.id,
                            device_id: this.selectedOmni().id,
                            has_full_access: this.selectedOmni() ? false : true,
                            is_admin: false,
                            expires_at: null,
                        })
                        .then(() => {
                            this.alertService.createAlert({
                                title: $localize`:@@PLAYER_INVITED:Player invited`,
                                icon: 'success',
                            });
                        })
                        .catch((err) => this.alertService.errorFromApi(err));
                }
            });
    }

    public repairOmni(): void {
        this.modalService.openModal<SmartDeviceFirmwareUpdateDialogPayload>({
            component: SmartDeviceFirmwareUpdateDialogComponent,
            componentProps: {
                updateMode: FirmwareUpdateMode.REPAIR,
            },
            cssClass: slideDialogClasses,
            backdropDismiss: false,
        });
    }

    public openCalibrationImages(): void {
        this.modalService.openModal({
            component: OmniCalibrationImagesComponent,
            cssClass: slideDialogClasses,
            backdropDismiss: true,
        });
    }

    public promptBeta(): void {
        this.modalService
            .openModal<any, OmniBetaOptInResponse>({
                component: OmniBetaOptInComponent,
                componentProps: {},
                cssClass: slideDialogClasses,
                backdropDismiss: true,
            })
            .then((dialogRes) => {
                if (dialogRes) {
                    if (dialogRes.leftBeta) {
                        this.applyLatestStableFirmware();
                    } else {
                        this.overTheAirUpdateService.checkForUpdates(this.selectedOmni().id);
                    }
                }
            });
    }

    public rebootOmni(): void {
        this.modalService
            .openModal<any, User>({
                component: PromptDialogComponent,
                componentProps: {
                    title: $localize`:@@REBOOT_DEVICE:Reboot device`,
                    text: $localize`:@@REBOOTING_DEVICE:Do you want to reboot this device?`,
                    cancelText: $localize`:@@CANCEL:Cancel`,
                    confirmText: $localize`:@@CONFIRM:Confirm`,
                    confirmColor: 'red',
                } as AlertPayload,
                cssClass: centerDialogClasses,
                showBackdrop: true,
                backdropDismiss: false,
            })
            .then((dialogRes) => {
                if (dialogRes) {
                    this.omniCommunicationService.rebootOmni();
                }
            });
    }

    public removeOmni(): void {
        this.modalService
            .openModal<any, User>({
                component: PromptDialogComponent,
                componentProps: {
                    title: $localize`:@@DELETE_DEVICE:Remove device`,
                    text: $localize`:@@DELETING_DEVICE:Do you want to remove this device?`,
                    cancelText: $localize`:@@CANCEL:Cancel`,
                    confirmText: $localize`:@@CONFIRM:Confirm`,
                    confirmColor: 'red',
                } as AlertPayload,
                cssClass: centerDialogClasses,
                showBackdrop: true,
                backdropDismiss: false,
            })
            .then((dialogRes) => {
                if (dialogRes) {
                    this.smartDeviceApiService
                        .deleteSmartDeviceById({ smartDeviceId: this.selectedOmni().id })
                        .then(() => {
                            this.smartDevicesStateService.removeDeviceFromRefreshList(this.selectedOmni().id);
                            this.smartDevicesStateService.removeDeviceFromNetwork(
                                this.selectedOmni().network_id,
                                this.selectedOmni().id
                            );
                            this.omniCommunicationService.disconnectActiveSocket();
                            this.omniStateService.clearCurrentSmartDevice();
                            this.navController.back();
                        });
                }
            });
    }

    public changeFirmware(): void {
        this.modalService
            .openModal<SmartDevicesChangeFirmwarePayload, SmartDeviceFirmware>({
                component: SmartDevicesChangeFirmwareComponent,
                componentProps: {
                    smartDeviceType: 'omni_scoring',
                },
                cssClass: centerDialogClasses,
                showBackdrop: true,
                backdropDismiss: false,
            })
            .then((firmware) => {
                if (firmware) {
                    this.modalService.openModal<SmartDeviceFirmwareUpdateDialogPayload>({
                        component: SmartDeviceFirmwareUpdateDialogComponent,
                        componentProps: {
                            updateMode: FirmwareUpdateMode.FORCE,
                            firmware: firmware,
                        },
                        cssClass: slideDialogClasses,
                        backdropDismiss: true,
                    });
                }
            });
    }

    private applyLatestStableFirmware(): void {
        if (
            this.omniStateService.currentSmartDevice().firmware?.beta &&
            this.omniStateService.currentSmartDevice().firmware?.prev_smart_devices_firmware
        ) {
            //If the current software was still Beta and there is a stable previous version > initiate the rollback
            this.modalService.openModal<SmartDeviceFirmwareUpdateDialogPayload>({
                component: SmartDeviceFirmwareUpdateDialogComponent,
                componentProps: {
                    updateMode: FirmwareUpdateMode.ROLLBACK,
                },
                cssClass: slideDialogClasses,
                backdropDismiss: true,
            });
        }
    }
}
